import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Gem, ShieldCheck, Clock } from "lucide-react";

export const Route = createFileRoute("/bespoke")({
  head: () => ({
    meta: [
      { title: "Bespoke & Concierge, HOWDEN" },
      {
        name: "description",
        content:
          "Commission a one-of-a-kind lab-grown diamond piece. Share your vision and HOWDEN's master craftspeople will design and hand-make it to your specification.",
      },
      { property: "og:title", content: "Bespoke & Concierge, HOWDEN" },
      {
        property: "og:description",
        content:
          "Commission a one-of-a-kind lab-grown diamond piece. Share your vision and HOWDEN's master craftspeople will design and hand-make it to your specification.",
      },
      { property: "og:url", content: "https://howdendiamonds.com/bespoke" },
    ],
    links: [{ rel: "canonical", href: "https://howdendiamonds.com/bespoke" }],
  }),
  component: BespokePage,
});

const SHAPES = [
  "Round",
  "Oval",
  "Emerald",
  "Cushion",
  "Pear",
  "Radiant",
  "Princess",
  "Marquise",
  "Asscher",
  "Open to recommendation",
];

const BUDGETS = [
  "Under $2,000",
  "$2,000 – $5,000",
  "$5,000 – $10,000",
  "$10,000 – $25,000",
  "$25,000+",
];

const TIMELINES = [
  "Within 2 weeks",
  "Within a month",
  "1–3 months",
  "No rush",
];

function BespokePage() {
  const [sending, setSending] = useState(false);
  const [reference, setReference] = useState<string | null>(null);

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return setReference(null);
    setReference(f.name);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSending(true);
    setTimeout(() => {
      setSending(false);
      (e.target as HTMLFormElement).reset();
      setReference(null);
      toast.success("Concierge request received", {
        description:
          "A member of the House will respond within one business day.",
      });
    }, 900);
  };

  return (
    <>
      {/* Hero */}
      <section className="border-b border-hairline">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 py-20 lg:grid-cols-12 lg:gap-24 lg:px-12 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <p className="text-xs uppercase tracking-luxe text-muted-foreground">
              Private commission
            </p>
            <h1 className="mt-6 font-serif text-5xl font-light leading-[1.05] lg:text-7xl">
              Bespoke
              <br />
              <em className="font-light italic text-accent-foreground/80">
                commissions
              </em>{" "}
              by hand.
            </h1>
            <p className="mt-10 max-w-md text-base leading-relaxed text-muted-foreground">
              Tell us what you have in mind, a ring, a pendant, an heirloom
              re-imagined, and we will design it with you and assign it to the
              atelier whose craft tradition suits it best. Made by hand, from
              first sketch to final polish.
            </p>
          </motion.div>

          <div className="space-y-8 lg:col-span-5 lg:pt-12">
            {[
              {
                icon: Gem,
                title: "Designed and made by hand",
                body: "Every commission is hand-built by a master goldsmith, never cast in bulk, never assembled on a line.",
              },
              {
                icon: ShieldCheck,
                title: "IGI / GIA on every stone",
                body: "Independent grading reports issued before any commitment is made.",
              },
              {
                icon: Clock,
                title: "Typical lead time: 3–6 weeks",
                body: "Most pieces are completed within six weeks. Intricate settings or rare stones, longer.",
              },
            ].map((p) => (
              <div key={p.title} className="flex gap-5">
                <p.icon
                  className="mt-1 h-4 w-4 shrink-0 text-accent-foreground/70"
                  strokeWidth={1.25}
                />
                <div>
                  <h3 className="font-serif text-lg font-light">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {p.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="mx-auto max-w-3xl px-6 py-20 lg:px-12 lg:py-32">
        <p className="text-xs uppercase tracking-luxe text-muted-foreground">
          Commission request
        </p>
        <h2 className="mt-4 font-serif text-3xl font-light lg:text-4xl">
          Tell us what you'd like us to make.
        </h2>

        <form onSubmit={handleSubmit} className="mt-16 space-y-10">
          <div className="grid gap-8 sm:grid-cols-2">
            <Field label="Name" name="name" required />
            <Field label="Email" name="email" type="email" required />
          </div>

          <SelectField label="Shape" name="shape" options={SHAPES} required />

          <div className="grid gap-8 sm:grid-cols-2">
            <Field
              label="Carat range"
              name="carat"
              placeholder="e.g. 1.5 – 2.0"
            />
            <SelectField label="Budget" name="budget" options={BUDGETS} required />
          </div>

          <SelectField label="Timeline" name="timeline" options={TIMELINES} />

          <div>
            <label className="text-xs uppercase tracking-luxe text-muted-foreground">
              Notes
            </label>
            <textarea
              name="notes"
              rows={5}
              placeholder="Colour / clarity preferences, intended setting, anything else."
              className="mt-3 w-full border-b border-hairline bg-transparent py-3 text-base outline-none transition-colors placeholder:text-muted-foreground/50 focus:border-foreground"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-luxe text-muted-foreground">
              Reference image (optional)
            </label>
            <div className="mt-3 flex items-center gap-4 border-b border-hairline pb-3">
              <label className="cursor-pointer border border-hairline px-4 py-2 text-xs uppercase tracking-luxe transition-colors hover:border-foreground">
                Choose file
                <input
                  type="file"
                  name="reference"
                  accept="image/*"
                  className="hidden"
                  onChange={onFile}
                />
              </label>
              <span className="text-xs text-muted-foreground">
                {reference ?? "No file selected"}
              </span>
            </div>
          </div>

          <button
            type="submit"
            disabled={sending}
            className="h-12 w-full bg-foreground text-xs uppercase tracking-luxe text-background transition-opacity hover:opacity-90 disabled:opacity-50 sm:w-auto sm:px-12"
          >
            {sending ? "Sending…" : "Submit request"}
          </button>
        </form>
      </section>
    </>
  );
}

function Field({
  label,
  name,
  type = "text",
  required,
  placeholder,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="text-xs uppercase tracking-luxe text-muted-foreground">
        {label}
        {required && " *"}
      </label>
      <input
        type={type}
        name={name}
        required={required}
        placeholder={placeholder}
        className="mt-3 w-full border-b border-hairline bg-transparent py-3 text-base outline-none transition-colors placeholder:text-muted-foreground/50 focus:border-foreground"
      />
    </div>
  );
}

function SelectField({
  label,
  name,
  options,
  required,
}: {
  label: string;
  name: string;
  options: readonly string[];
  required?: boolean;
}) {
  return (
    <div>
      <label className="text-xs uppercase tracking-luxe text-muted-foreground">
        {label}
        {required && " *"}
      </label>
      <select
        name={name}
        required={required}
        defaultValue=""
        className="mt-3 w-full border-b border-hairline bg-transparent py-3 text-base outline-none transition-colors focus:border-foreground"
      >
        <option value="" disabled>
          Select…
        </option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
